import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Mic, MicOff } from "lucide-react";
import { toast } from "sonner";

/**
 * Botão de microfone para preencher um input/textarea por voz (PT-BR).
 * Usa Web Speech API nativa do navegador (Chrome/Safari).
 *
 * Props:
 *  - onTranscript(text): callback com texto final ou parcial
 *  - size: "icon" | "sm" (default icon)
 *  - testId
 */
export default function VoiceInputButton({ onTranscript, size = "icon", testId = "voice-button" }) {
  const recognitionRef = useRef(null);
  const [listening, setListening] = useState(false);

  const supports = typeof window !== "undefined" && (
    "webkitSpeechRecognition" in window || "SpeechRecognition" in window
  );

  if (!supports) return null;

  const start = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = "pt-BR";
    rec.interimResults = true;
    rec.continuous = false;
    rec.onresult = (e) => {
      let text = "";
      for (let i = 0; i < e.results.length; i++) {
        text += e.results[i][0].transcript;
      }
      onTranscript?.(text);
    };
    rec.onerror = (e) => {
      setListening(false);
      if (e.error !== "no-speech") toast.error("Erro no microfone: " + e.error);
    };
    rec.onend = () => setListening(false);
    recognitionRef.current = rec;
    try {
      rec.start();
      setListening(true);
    } catch {
      setListening(false);
    }
  };

  const stop = () => {
    try { recognitionRef.current?.stop(); } catch {}
    setListening(false);
  };

  return (
    <Button
      type="button"
      size={size}
      variant={listening ? "default" : "ghost"}
      onClick={listening ? stop : start}
      className={listening ? "animate-pulse" : ""}
      data-testid={testId}
      title={listening ? "Parar gravação" : "Falar"}
    >
      {listening ? <MicOff className="h-4 w-4" strokeWidth={1.5} /> : <Mic className="h-4 w-4" strokeWidth={1.5} />}
    </Button>
  );
}
